import { toast } from "react-toastify";
import { HttpStatusCode } from "axios";
import { useState } from "react";

/**
 * Selection meaning "deliver every payment type". Stored server-side as the absence of rows.
 */
export const ALL_PAYMENT_TYPES = "ALL";

/**
 * Payment types a subscriber can filter on, in the order they appear in the picker.
 *
 * These must stay in step with WebHookPaymentTypes in
 * src/TargetPayments.Common/Constants/WebHookPaymentTypes.cs — the modify request is validated
 * against that list server-side, so a value only present here is rejected on save.
 *
 * "SavedCard" covers both a charge against an already-stored card and the tokenisation that
 * stored it; the webhook contract does not distinguish the two.
 */
export const PAYMENT_TYPE_OPTIONS = [
  ALL_PAYMENT_TYPES,
  "Terminal",
  "Manual",
  "ACH",
  "PayByLink",
  "SavedCard",
  "Ecommerce",
];

export const validateUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export const copyText = async (text: string) => {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    toast.success("Content copied successfully.✅");
  } else {
    copyToClipboardFallback(text);
  }
};

export const copyToClipboardFallback = (text: string) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.select();
  try {
    document.execCommand("copy");
    console.log("Copied using fallback:", text);
  } catch (err) {
    console.error("Fallback copy failed", err);
  }
  document.body.removeChild(textArea);
};

export function handleApiError(error: any) {
  if (error?.status === HttpStatusCode.BadRequest && error.data?.errors) {
    const errors = error.data.errors;

    Object.entries(errors).forEach(([field, messages]) => {
      (messages as string[]).forEach((message) => {
        toast.error(`${message}`, {
          autoClose: 5000,
        });
      });
    });
  } else {
    toast.error("An unexpected error occurred.");
  }
}

export const useCommonKVP = () => {
  const [rows, setRows] = useState([{ key: "", value: "" }]);

  const handleKVPChange = (
    index: number,
    field: "key" | "value",
    value: string
  ) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  const handleAddRow = () => {
    setRows([...rows, { key: "", value: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  const handleHeaderParse = (headerString: string) => {
    const parsedRows = headerString.split(",").map((pair) => {
      const colonIndex = pair.indexOf(":");
      return {
        key: pair.slice(0, colonIndex).trim(),
        value: pair.slice(colonIndex + 1).trim(),
      };
    });
    setRows(parsedRows);
  };

  return {rows, handleKVPChange, handleAddRow, handleRemoveRow , handleHeaderParse};
};
